<template>
    <div class="demo-personalize">
        <ul v-if="brands.length" class="demo-personalize__brands">
            <li v-for="brand in brands" :key="brand.id" class="demo-personalize__brand">
                <img :src="brand.avatar || 'img/avatar.jpg'" class="demo-personalize__brand-avatar" />
                <span>{{ brand.name }}</span>
                <i class="fa-solid fa-xmark" @click="remove(brand.id)"></i>
            </li>
        </ul>

        <div class="demo-personalize__trigger" @click="open = !open">
            <i class="fa-solid fa-plus"></i>
            <span>{{ $t('dash.demo.personalize.toggle') }}</span>
        </div>

        <!-- Full-screen centered modal (its own backdrop) - the inner box reuses the exact
             markup/classes of formOverlay/selectMention.vue (formOverlay.scss) so the content
             itself looks like any other Post Creator overlay, just centered instead of anchored. -->
        <div v-if="open" class="demo-personalize__backdrop" @click.self="open = false">
            <div class="form-overlay select-mention demo-personalize__modal">
                <div class="steep1">
                    <div class="nav-network hide-scrollbar">
                        <div class="list-item-network">
                            <div class="item-network" :class="{active: networkSelected === 'facebook'}" @click="selectNetwork('facebook')"><i class="fa-brands fa-facebook"></i></div>
                            <div class="item-network" :class="{active: networkSelected === 'instagram'}" @click="selectNetwork('instagram')"><i class="fa-brands fa-instagram"></i></div>
                            <div class="item-network" :class="{active: networkSelected === 'linkedin'}" @click="selectNetwork('linkedin')"><i class="fa-brands fa-linkedin"></i></div>
                            <div class="item-network" :class="{active: networkSelected === 'tiktok'}" @click="selectNetwork('tiktok')"><i class="fa-brands fa-tiktok"></i></div>
                        </div>
                    </div>
                    <div class="content searchMention">
                        <div class="input">
                            <i class="fa-regular fa-magnifying-glass"></i>
                            <input :placeholder="$t('dash.demo.personalize.placeholder')" v-model="search" ref="inputSearch">
                        </div>
                        <div class="list-item hide-scrollbar">
                            <span class="loader" v-if="loading"></span>
                            <template v-else-if="results.length">
                                <div class="item" v-for="result in results" :key="result.id" @click="choose(result)">
                                    <img :src="result.image || 'img/avatar.jpg'" alt="">
                                    <div class="info">
                                        <p class="username">{{ result.name }}</p>
                                    </div>
                                </div>
                            </template>
                            <p v-else-if="search" class="no-result">{{ $t('dash.demo.personalize.error') }}</p>
                        </div>
                    </div>
                </div>
                <div class="btn-secondary" @click="open = false">{{ $t('dash.button.close') }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import { EventBus } from '@/event-bus'
import BotsMixin from '@/components/mixins/Bots.vue'
import { loadCustomBrands, addCustomBrand, removeCustomBrand } from './fakeData.js'

export default {
    name: 'PersonalizePages',

    mixins: [BotsMixin],

    data() {
        return {
            open: false,
            networkSelected: 'facebook',
            search: '',
            loading: false,
            results: [],
            searchTimeout: null,
            brands: loadCustomBrands()
        }
    },

    watch: {
        search() {
            clearTimeout(this.searchTimeout)
            if (!this.search) {
                this.results = []
                return
            }
            this.searchTimeout = setTimeout(this.runSearch, 1000)
        }
    },

    mounted() {
        EventBus.$on('demo/openPersonalize', this.openFromWelcomePopup)
    },

    beforeDestroy() {
        clearTimeout(this.searchTimeout)
        EventBus.$off('demo/openPersonalize', this.openFromWelcomePopup)
    },

    methods: {
        openFromWelcomePopup() {
            this.open = true
        },

        selectNetwork(network) {
            this.networkSelected = network
            this.results = []
            if (this.search) this.runSearch()
        },

        // Any fake page of that network works - only its `network` field is read
        // (_demoRequestOptions, Bots.vue), the id itself gets overwritten server-side.
        pageIdForNetwork(network) {
            const allPages = this.$store.getters['selectPage/selectPageAllPages']
            const match = Object.values(allPages).find(p => p.network === network)
            return match ? match.id : null
        },

        normalizeMention(mention) {
            if (this.networkSelected === 'facebook') {
                return { id: mention.id, name: mention.name, image: mention.picture && mention.picture.data && mention.picture.data.url }
            }
            return { id: mention.id, name: mention.name, image: mention.image }
        },

        async runSearch() {
            const pageId = this.pageIdForNetwork(this.networkSelected)
            if (!pageId) {
                this.results = []
                return
            }

            this.loading = true
            try {
                const data = await this.getMentionsByApi(pageId, this.search, false)
                const mentions = (data && data.mentions) || {}
                const results = Object.values(mentions).map(mention => this.normalizeMention(mention))

                if (this.networkSelected === 'facebook') {
                    // Facebook's search doesn't include the picture inline for every result - a
                    // second call per missing avatar is required (same as selectMention.vue).
                    await Promise.all(results.filter(r => !r.image).map(async result => {
                        const res = await this.getAvatarMentionByApi(pageId, result.id)
                        const url = res && res.body && res.body.avatar && res.body.avatar.data && res.body.avatar.data.url
                        if (url) result.image = url
                    }))
                }

                this.results = results
            } catch (error) {
                this.results = []
            } finally {
                this.loading = false
            }
        },

        choose(result) {
            this.brands = addCustomBrand({ name: result.name, avatar: result.image })
            this.open = false
            this.search = ''
            this.results = []
            this.$emit('personalized')
        },

        remove(brandId) {
            this.brands = removeCustomBrand(brandId)
            this.$emit('personalized')
        }
    }
}
</script>

<style lang="scss" scoped>
.demo-personalize {
    position: relative;
    padding: 0 16px;

    &__brands {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        list-style: none;
        margin: 0 0 8px;
        padding: 0;
    }

    &__brand {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 3px 8px;
        border-radius: 20px;
        background: #f2f2f2;
        font-size: 12px;

        i {
            cursor: pointer;
            opacity: 0.6;
        }
    }

    &__brand-avatar {
        width: 18px;
        height: 18px;
        border-radius: 50%;
        object-fit: cover;
    }

    &__trigger {
        display: flex;
        align-items: center;
        gap: 8px;
        height: 32px;
        padding: 0 10.4px;
        margin: 4px 0 8px;
        border-radius: 5.6px;
        border: solid 1.6px #E3D2EE;
        background: rgba(182, 73, 133, 0.06);
        color: #121826;
        font-size: 12.8px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s;

        i {
            color: #B64985;
            font-size: 14px;
        }

        &:hover {
            background: rgba(182, 73, 133, 0.12);
        }
    }

    &__backdrop {
        position: fixed;
        inset: 0;
        z-index: 1000;
        background: rgba(0, 0, 0, 0.6);
        display: flex;
        align-items: center;
        justify-content: center;
    }

    // .form-overlay.select-mention (formOverlay.scss, global) is built to float anchored next to
    // a toolbar button (position: absolute; top: 50%; left: -80px; transform: translate(-100%,
    // -50%)) - overridden here so the exact same inner content instead sits centered in our own
    // full-screen backdrop above. !important is intentional: overriding a shared, unscoped
    // stylesheet from a scoped component reliably needs it here.
    &__modal {
        position: static !important;
        top: auto !important;
        left: auto !important;
        right: auto !important;
        transform: none !important;
        align-items: flex-end !important;
        width: 420px;
        max-width: 90vw;

        .steep1 {
            width: 100%;
        }
    }
}
</style>
